import React, { useCallback, useState } from 'react'
import { Check, ExpandMore } from '@mui/icons-material'

import { useEscapeKey } from '../hooks/useEscapeKey'
import type { Tenant } from '../types/tenant'

type TenantSelectorProps = {
  tenants: Tenant[]
  selectedTenant: Tenant | null
  onTenantChange: (tenant: Tenant) => void
  className?: string
  // When true the control is shown but greyed out and cannot be opened. Used on
  // pages that act across tenants (e.g. Org Management), where switching the
  // globally-selected tenant would be ambiguous.
  disabled?: boolean
}

// @purpose Dropdown for switching the active organization/tenant, showing name and domain for each; pass `disabled` to lock it on cross-tenant pages
const TenantSelector = ({
  tenants,
  selectedTenant,
  onTenantChange,
  className = '',
  disabled = false,
}: TenantSelectorProps): React.JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)

  // PENG-13715: dismiss with Escape (backdrop-click already dismisses).
  const closeDropdown = useCallback(() => setIsOpen(false), [])
  useEscapeKey(isOpen, closeDropdown)

  const handleToggle = () => {
    if (disabled) return
    setIsOpen(!isOpen)
  }

  const handleTenantSelect = (tenant: Tenant) => {
    onTenantChange(tenant)
    setIsOpen(false)
  }

  return (
    <div className={`tenant-selector relative ${className}`}>
      {/* Trigger Button */}
      <button
        onClick={handleToggle}
        disabled={disabled}
        data-testid="tenant-selector-trigger"
        className={`flex items-center gap-2 px-4 py-1 bg-header-pill border border-header-line rounded-lg text-header-icon transition-colors ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-header-line'
        }`}
      >
        <div className="text-left">
          <div className="font-medium text-white text-sm">
            {selectedTenant?.name || 'Select Organization'}
          </div>
          <div className="text-header-muted text-xs">
            {selectedTenant?.domain || 'No domain set'}
          </div>
        </div>
        <ExpandMore sx={{ fontSize: 16 }} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Content — no top-of-panel header (parity with the
              Filters and Time Range dropdowns): the trigger button placeholder
              ('Select Organization' / current org name) plus the per-row check
              icon are enough to communicate what this list does. */}
          <div className="absolute top-full left-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20">
            {/* Tenant List */}
            <div className="py-2">
              {tenants.map(tenant => (
                <button
                  key={tenant.id}
                  onClick={() => handleTenantSelect(tenant)}
                  className={`w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                    tenant.id === selectedTenant?.id
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : ''
                  }`}
                >
                  <div className="text-left">
                    <div className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                      {tenant.name}
                    </div>
                    <div className="text-gray-500 dark:text-gray-400 text-xs">
                      {tenant.domain}
                    </div>
                  </div>
                  {tenant.id === selectedTenant?.id && (
                    <div className="text-blue-600 dark:text-blue-400">
                      <Check sx={{ fontSize: 16 }} />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default TenantSelector
